// ===== BEGIN FILE: src/components/UserForm.tsx =====
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { DateTime } from 'luxon';
import { Box, FormControl, FormLabel, Input, Radio, RadioGroup, Select, Option, Typography, FormHelperText, Sheet, IconButton, Tooltip } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AudioGuide from './AudioGuide';
import { useSession } from '../session/useSession';
import {
  searchCitiesSmart,
  type CityOption,
  POPULAR_CITY_OPTIONS,
  rememberCitySelection
} from '../utils/cityUtils';
import { supabase } from '../utils/supabaseClient';

type Gender = 'male' | 'female' | 'other';

type Relationship = 'Self' | 'Spouse' | 'Father' | 'Mother' | 'Son' | 'Daughter' | 'Brother' | 'Sister' | 'Grandfather (Dada)' | 'Grandmother (Daadi)' | 'Grandfather (Nana)' | 'Grandmother (Naani)' | 'Uncle' | 'Aunt' | 'Other';

type FormState = {
  fullName: string;
  gender: Gender;
  relationship: Relationship;
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
  second: string;
  place: CityOption | null;
};

// Normalize any incoming gender text to our union
function normalizeGender(g: unknown): Gender {
  const x = (g ?? '').toString().trim().toLowerCase();
  if (x === 'male' || x === 'm') return 'male';
  if (x === 'female' || x === 'f') return 'female';
  return 'other';
}

// Safe int â†' number | null (no NaN)
function toIntOrNull(s: string): number | null {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isInteger(n) ? n : null;
}

// Map a lowercase gender to possible DB variants (for retry on CHECK failure)
function genderVariants(base: Gender): string[] {
  const title = base === 'male' ? 'Male' : base === 'female' ? 'Female' : 'Other';
  const code = base === 'male' ? 'M' : base === 'female' ? 'F' : 'O';
  return [base, title, code];
}

function formatUtcOffset(tz: string): string | null {
  try {
    const sample = DateTime.now().setZone(tz);
    if (!sample.isValid) return null;
    const minutes = sample.offset; // minutes east of UTC
    const sign = minutes >= 0 ? '+' : '-';
    const abs = Math.abs(minutes);
    const hours = Math.floor(abs / 60);
    const mins = abs % 60;
    return `UTC${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  } catch {
    return null;
  }
}

export default function UserForm({ wizardMode = false, embedded = false }: { wizardMode?: boolean; embedded?: boolean }) {
  const { user } = useSession();
  const [state, setState] = useState<FormState>({
    fullName: '',
    gender: 'male',
    relationship: 'Self',
    day: '',
    month: '',
    year: '',
    hour: '',
    minute: '',
    second: '',
    place: null,
  });
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Audio cue state — tracks which form field to highlight during audio guide playback
  const [activeCue, setActiveCue] = useState<string | null>(null);

  const BIRTH_FORM_CUES = [
    { from: 15.08, to: 19.10, fieldCue: 'name' },
    { from: 17.44, to: 19.10, fieldCue: 'gender' },
    { from: 19.10, to: 28.00, fieldCue: 'relationship' },
    { from: 28.96, to: 31.56, fieldCue: 'dob' },
    { from: 31.56, to: 43.04, fieldCue: 'tob' },
    { from: 43.04, to: 54.06, fieldCue: 'city' },
    { from: 46.60, to: 54.06, fieldCue: 'coords' },
    { from: 54.76, to: 65.34, fieldCue: 'cant-find-city' },
    { from: 65.34, to: 70.58, fieldCue: 'save' },
  ] as const;

  const handleAudioTime = (t: number) => {
    // When multiple cues overlap (name/gender 17.44–19.10), the last match wins (gender)
    let found: string | null = null;
    for (const cue of BIRTH_FORM_CUES) {
      if (t >= cue.from && t < cue.to) found = cue.fieldCue;
    }
    setActiveCue(found);
  };

  // Inject the pulse keyframe CSS once, shared across re-renders
  useEffect(() => {
    const STYLE_ID = 'audio-cue-pulse-style';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes audioCuePulse {
        0%, 100% { outline-color: var(--joy-palette-primary-400); outline-offset: 4px; }
        50%       { outline-color: var(--joy-palette-primary-300); outline-offset: 9px; }
      }
      .audio-cue-active {
        outline: 2px solid var(--joy-palette-primary-400) !important;
        outline-offset: 4px;
        border-radius: 8px;
        animation: audioCuePulse 1.4s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Toggle .audio-cue-active on the target field wrapper — runs only when activeCue changes
  useEffect(() => {
    document.querySelectorAll<HTMLElement>('.audio-cue-active').forEach(el => el.classList.remove('audio-cue-active'));
    if (activeCue) {
      document.querySelector<HTMLElement>(`[data-audio-cue="${activeCue}"]`)?.classList.add('audio-cue-active');
    }
    return () => {
      document.querySelectorAll<HTMLElement>('.audio-cue-active').forEach(el => el.classList.remove('audio-cue-active'));
    };
  }, [activeCue]);

  // Track the user's typed text in the city box (used to store the exact phrase with the selection)
  const [cityInput, setCityInput] = useState('');

  // Manual coordinates fallback
  const [showManualCoords, setShowManualCoords] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLon, setManualLon] = useState('');
  const [manualVerifying, setManualVerifying] = useState(false);
  type ManualVerified = { city: string; region: string; country: string; tz: string; label: string };
  const [manualVerified, setManualVerified] = useState<ManualVerified | null>(null);
  const [manualError, setManualError] = useState<string | null>(null);

  // City suggestion state (shown when coords can't be auto-identified)
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [suggestCity, setSuggestCity] = useState('');
  const [suggestState, setSuggestState] = useState('');
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestSuccess, setSuggestSuccess] = useState(false);

  // Sanitize user text input: strip HTML tags, limit chars, keep safe chars only
  const sanitizeLocationInput = (val: string) =>
    val.replace(/<[^>]*>/g, '').replace(/[^a-zA-Z0-9\s\-',().]/g, '').slice(0, 100);

  const verifyManualCoords = async () => {
    setManualError(null);
    setManualVerified(null);
    setShowSuggestForm(false);
    setSuggestSuccess(false);
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90)
      return setManualError('Latitude must be a number between -90 and 90.');
    if (!Number.isFinite(lon) || lon < -180 || lon > 180)
      return setManualError('Longitude must be a number between -180 and 180.');
    setManualVerifying(true);
    try {
      // Route through edge function (secure — API key stays server-side)
      const { data, error } = await supabase.functions.invoke('opencage', {
        body: {
          q: `${lat} ${lon}`,
          reverse: true,
        },
      });
      if (error || !data?.results?.length) {
        // Could not identify via reverse geocoding → offer manual name entry
        setManualError('Could not auto-identify this location. Please enter the city name below so we can add it to our database.');
        setShowSuggestForm(true);
        setSuggestCity('');
        setSuggestState('');
        return;
      }
      const r = data.results[0];
      const cityName = r.city || r.label?.split(',')[0]?.trim() || '';
      const region = r.region || '';
      const country = r.country || '';
      const tz = r.tz || 'Asia/Kolkata';
      const label = r.label || [cityName, region, country].filter(Boolean).join(', ');
      setManualVerified({ city: cityName, region, country, tz, label });
    } catch {
      setManualError('Verification failed. Please check your connection and try again.');
      setShowSuggestForm(true);
    } finally {
      setManualVerifying(false);
    }
  };

  const applyManualCoords = (overrideLabel?: string) => {
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);
    const verified = manualVerified;
    // Allow applying even without verified data if user provided a manual label
    const label = overrideLabel || verified?.label || '';
    const tz = verified?.tz || 'Asia/Kolkata';
    if (!label) return;
    const opt: CityOption = {
      label,
      value: `${lat.toFixed(5)},${lon.toFixed(5)}|${tz}`,
      city: overrideLabel ? suggestCity : verified?.city || '',
      region: overrideLabel ? suggestState : verified?.region || '',
      country: verified?.country || '',
      lat,
      lon,
      lng: lon,
      tz,
      timezone: tz,
      source: 'static',
    };
    onChangeField('place', opt);
    setShowManualCoords(false);
    setManualVerified(null);
    setManualLat('');
    setManualLon('');
    setShowSuggestForm(false);
  };

  const submitCitySuggestion = async (tzFromVerify?: string) => {
    const cityClean = sanitizeLocationInput(suggestCity).trim();
    const stateClean = sanitizeLocationInput(suggestState).trim();
    if (!cityClean) return;
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    const tz = tzFromVerify || manualVerified?.tz || 'Asia/Kolkata';
    setSuggestSubmitting(true);
    try {
      const { error } = await supabase.from('city_suggestions').insert({
        lat,
        lon,
        city_name: cityClean,
        state_name: stateClean || null,
        country_name: null,
        tz,
      });
      if (error) {
        if (error.code === '23505') {
          // Duplicate — still count as success for UX
        } else if ((error as any).code === '42501' || error.message?.includes('row-level')) {
          setManualError('Suggestion limit reached (max 5 per day). Please try again tomorrow.');
          return;
        } else {
          throw error;
        }
      }
      setSuggestSuccess(true);
      // Apply coords with user-provided label immediately
      const label = [cityClean, stateClean].filter(Boolean).join(', ');
      applyManualCoords(label);
    } catch {
      setManualError('Could not submit suggestion. Please try again.');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  // Refs for auto-focus navigation
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const secondRef = useRef<HTMLInputElement>(null);

  // Debounced city loader (calls new searchCitiesSmart)
  const loadCityOptions = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastReject: ((reason?: any) => void) | null = null;

    return (input: string) =>
      new Promise<CityOption[]>((resolve, reject) => {
        if (lastReject) lastReject('cancelled');
        lastReject = reject;
        if (timer) clearTimeout(timer);

        timer = setTimeout(async () => {
          try {
            const res = await searchCitiesSmart(input);
            resolve(res);
          } catch (e) {
            reject(e);
          } finally {
            timer = null;
            lastReject = null;
          }
        }, 300);
      });
  }, []);

  function onChangeField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState(s => ({ ...s, [key]: value }));
    setMsg(null);
    setErr(null);
  }

  function asInt(s: string) {
    if (s == null || s === '') return NaN;
    const n = Number(s);
    return Number.isInteger(n) ? n : NaN;
  }

  function validate(): string | null {
    if (!user) return 'You must be logged in.';
    if (!state.fullName.trim()) return 'Please enter full name.';

    const d = asInt(state.day);
    const m = asInt(state.month);
    const y = asInt(state.year);
  if (!(d >= 1 && d <= 31)) return 'Day must be 1–31.';
  if (!(m >= 1 && m <= 12)) return 'Month must be 1–12.';
  if (!(y >= 1900 && y <= 2100)) return 'Year must be 1900–2100.';

    const hh = asInt(state.hour);
    const mm = asInt(state.minute);
    const ss = asInt(state.second);
  if (!(hh >= 0 && hh <= 23)) return 'Hour must be 0–23.';
  if (!(mm >= 0 && mm <= 59)) return 'Minute must be 0–59.';
  if (!(ss >= 0 && ss <= 59)) return 'Second must be 0–59.';

    if (!state.place) return 'Please pick a birth city from the list.';

    const zone = state.place.tz;
    const dt = DateTime.fromObject(
      { year: y, month: m, day: d, hour: hh, minute: mm, second: ss },
      { zone }
    );
    if (!dt.isValid) return `Invalid date/time (${dt.invalidReason || 'check values'}).`;

    return null;
  }

  /* --------- Bridge with App.tsx ---------- */
  useEffect(() => {
    function toStr(n: number | null | undefined) { return (n ?? '').toString(); }
    function loadFromPayload(e: any) {
      const p = e?.detail?.payload;
      if (!p) return;
      const next: FormState = {
        fullName: (p.full_name ?? '') as string,
        gender: normalizeGender(p.gender),
        relationship: (p.relationship ?? 'Self') as Relationship,
        day: toStr(p.dob_day),
        month: toStr(p.dob_month),
        year: toStr(p.dob_year),
        hour: toStr(p.tob_hour),
        minute: toStr(p.tob_minute),
        second: toStr(p.tob_second),
        place: p.place_lat != null && p.place_lng != null ? (() => {
          const lat = Number(p.place_lat);
          const lon = Number(p.place_lng);
          const tz  = (p.place_timezone ?? 'UTC') as string;
          return {
            // CityOption (complete shape)
            label: (p.place_label ?? '') as string,
            value: `${lat.toFixed(5)},${lon.toFixed(5)}|${tz}`, // stable id
            city: (p.place_label ?? '') as string, // optional, fine to mirror label
            region: undefined,
            country: '',
            lat,
            lon,
            lng: lon,                // alias for UI code that expects lng
            tz,
            timezone: tz,           // alias for UI code that expects timezone
            source: 'static' as const
          };
        })() : null
      };
      setState(next);
      setMsg(null);
      setErr(null);
    }

    function onGetCurrent() {
      const s = stateRef.current;
      const payload = {
        full_name: s.fullName || null,
        gender: s.gender || null, // already normalized lower-case
        relationship: s.relationship || 'Self',
        dob_day: toIntOrNull(s.day),
        dob_month: toIntOrNull(s.month),
        dob_year: toIntOrNull(s.year),
        tob_hour: toIntOrNull(s.hour),
        tob_minute: toIntOrNull(s.minute),
        tob_second: toIntOrNull(s.second),
        place_label: s.place?.label ?? null,
        place_lat: s.place?.lat ?? null,
        place_lng: s.place?.lon ?? null,         // map lon -> DB place_lng
        place_timezone: s.place?.tz ?? null,     // tz
      };
      try {
        window.dispatchEvent(new CustomEvent('userform:current', { detail: { payload } }));
      } catch {}
    }

    window.addEventListener('userform:load', loadFromPayload);
    window.addEventListener('userform:load-from-saved', loadFromPayload);
    window.addEventListener('userform:get-current', onGetCurrent);
    return () => {
      window.removeEventListener('userform:load', loadFromPayload);
      window.removeEventListener('userform:load-from-saved', loadFromPayload);
      window.removeEventListener('userform:get-current', onGetCurrent);
    };
  }, []);

  // Listen for calculation completion from App.tsx
  useEffect(() => {
    const handleComplete = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setWorking(false);
      if (detail?.success) {
        setMsg('✅ Chart calculated and saved successfully!');
        console.log('[UserForm] Calculation complete for profile:', detail.profileId);
      } else {
        setErr(detail?.error || 'Calculation failed');
        console.error('[UserForm] Calculation failed:', detail?.error);
      }
    };
    window.addEventListener('calculation:complete', handleComplete);
    return () => window.removeEventListener('calculation:complete', handleComplete);
  }, []);

  // Try an upsert, and if the DB rejects on gender CHECK (23514), retry with other variants.
  async function upsertBirthProfile(ownerId: string, basePayload: any, primaryProfileId: string | null, existingProfileId?: string | null) {
    const variants = genderVariants(basePayload.gender as Gender);
    let lastErr: any = null;

    for (const g of variants) {
      const payload = { 
        ...basePayload, 
        gender: g,
        family_of_profile_id: primaryProfileId // Link to primary profile for family members
      };

      let error: any;
      
      // If editing existing profile, use direct UPDATE by ID (avoids coordinate rounding issues)
      if (existingProfileId) {
        console.log('[UserForm] Updating existing profile by ID:', existingProfileId);
        const result = await supabase
          .from('birth_profiles')
          .update(payload)
          .eq('id', existingProfileId)
          .eq('owner_id', ownerId);
        error = result.error;
      } else {
        // Creating new profile, use upsert with unique constraint
        console.log('[UserForm] Creating new profile via upsert');
        const result = await supabase
          .from('birth_profiles')
          .upsert([{ owner_id: ownerId, ...payload }], {
            onConflict: 'owner_id, full_name, dob_year, dob_month, dob_day, tob_hour, tob_minute, place_lat, place_lng, family_of_profile_id',
            ignoreDuplicates: false
          });
        error = result.error;
      }

      if (!error) return; // success
      if (error.code !== '23514') throw error; // 23514 = check_violation
      lastErr = error;
    }

    throw lastErr ?? new Error('Insert failed due to gender check constraint.');
  }

  // Primary action: persist family member and calculate
  async function onSaveAndCalculate(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setMsg(null);
    setErr(null);

    const v = validate();
    if (v) { setErr(v); return; }
    if (!state.place) { setErr('Please pick a birth city.'); return; }

    const zone = state.place.tz;
    const dtLocal = DateTime.fromObject(
      {
        year: Number(state.year),
        month: Number(state.month),
        day: Number(state.day),
        hour: Number(state.hour),
        minute: Number(state.minute),
        second: Number(state.second),
      },
      { zone }
    );

    try {
      setWorking(true);

      const ownerId = user!.id;
      const payload = {
        full_name: state.fullName.trim() || null,
        gender: state.gender, // start with normalized lower-case
        relationship: state.relationship, // Add relationship field to payload
        dob_day: toIntOrNull(state.day),
        dob_month: toIntOrNull(state.month),
        dob_year: toIntOrNull(state.year),
        tob_hour: toIntOrNull(state.hour),
        tob_minute: toIntOrNull(state.minute),
        tob_second: toIntOrNull(state.second),
        place_label: state.place.label,
        place_lat: state.place.lat,
        place_lng: state.place.lon,   // map lon -> DB place_lng
        place_timezone: state.place.tz,
      };

      // Determine primary profile ID for family linkage AND existing profile ID for updates
      // If creating 'Self' profile → null (they ARE the primary)
      // If creating family member → get selectedProfileId from App context
      let primaryProfileId: string | null = null;
      let existingProfileId: string | null = null;
      
      // Get selectedProfileId from App via custom event
      const getProfileEvent = new CustomEvent('userform:get-selected-profile');
      window.dispatchEvent(getProfileEvent);
      // @ts-ignore - selectedProfileId will be set by App listener
      const currentSelectedProfileId = (window as any).__selectedProfileId || null;

      if (currentSelectedProfileId && currentSelectedProfileId !== 'my_profile') {
        // Try to fetch the existing profile to check if it exists
        const { data: existingProfile } = await supabase
          .from('birth_profiles')
          .select('id, family_of_profile_id, relationship')
          .eq('id', currentSelectedProfileId)
          .single();
        
        if (existingProfile) {
          // Profile exists - we're editing, not creating
          existingProfileId = existingProfile.id;
          
          if (state.relationship && state.relationship !== 'Self') {
            // Editing/creating a family member
            if (existingProfile.family_of_profile_id) {
              // Selected profile IS a family member - preserve its linkage when editing
              primaryProfileId = existingProfile.family_of_profile_id;
              console.log('[UserForm] Editing existing family member, preserving family_of_profile_id:', primaryProfileId);
            } else {
              // Selected profile IS the primary - use it as the primary for new family members
              primaryProfileId = currentSelectedProfileId;
              existingProfileId = null; // Don't update primary when creating family member
              console.log('[UserForm] Creating new family member, linking to primary:', primaryProfileId);
            }
          } else {
            // Editing a Self profile - keep family_of_profile_id as null
            primaryProfileId = null;
            console.log('[UserForm] Editing existing Self profile:', existingProfileId);
          }
        } else if (state.relationship && state.relationship !== 'Self') {
          // Creating new family member
          primaryProfileId = currentSelectedProfileId;
          console.log('[UserForm] Creating new family member, linking to primary:', primaryProfileId);
        }
      }

      // Chart limit check — only applies when CREATING a new profile (not editing)
      if (!existingProfileId) {
        const { data: profileRow } = await supabase
          .from('profiles')
          .select('role, approved_family_quota, subscription_plan')
          .eq('id', ownerId)
          .single();

        const isAdmin = profileRow?.role === 'admin';

        if (!isAdmin) {
          // Determine effective chart limit:
          // approved_family_quota is the total chart slots (seeded per plan, overridable by admin)
          // Default plan limits: trial=4, silver=4, gold=6
          const planDefaults: Record<string, number> = { free_trial: 4, silver: 4, gold: 6 };
          const planMax = planDefaults[profileRow?.subscription_plan ?? 'free_trial'] ?? 4;
          const quota = profileRow?.approved_family_quota ?? 0;
          // Use admin-set quota when it has been explicitly set and differs from plan default
          const effectiveLimit = (quota > 0 && quota !== planMax) ? quota : planMax;

          const { count: existingCount } = await supabase
            .from('birth_profiles')
            .select('id', { count: 'exact', head: true })
            .eq('owner_id', ownerId);

          if ((existingCount ?? 0) >= effectiveLimit) {
            const planLabel = profileRow?.subscription_plan === 'silver' ? 'Silver' : profileRow?.subscription_plan === 'gold' ? 'Gold' : 'Free Trial';
            setErr(`${planLabel} plan allows up to ${effectiveLimit} charts (1 self + ${effectiveLimit - 1} family). Contact support to request more slots.`);
            setWorking(false);
            return;
          }
        }
      }

      // Try upsert/update with auto-fallbacks for gender
      await upsertBirthProfile(ownerId, payload, primaryProfileId, existingProfileId);

      // Ask FamilyManager to refresh its list
      try { window.dispatchEvent(new CustomEvent('family:refresh')); } catch {}

      // Trigger calculation in App.tsx
      const detail = {
        city: {
          label: state.place.label,
          city:  state.place.city ?? state.place.label,
          country: state.place.country ?? '',
          lat: state.place.lat,
          lon: state.place.lon,
          tz:  state.place.tz,
          source: state.place.source ?? 'city-timezones',
        },
        localDateTimeISO: dtLocal.toFormat("yyyy-LL-dd'T'HH:mm:ss"),
        fullPayload: payload, // used only to prefill the calculator form
      };
      window.dispatchEvent(new CustomEvent('calculator:saveAndCalculate', { detail }));

      setMsg('Saved. Calculating…');
      // Don't set setWorking(false) here - wait for calculation:complete event
    } catch (e: any) {
      setErr(e?.message || String(e));
      setWorking(false);
    }
  }

  return (
    <Sheet
      variant="outlined"
      sx={{
        mt: (wizardMode || embedded) ? 0 : 2,
        p: { xs: 2, sm: 3 },
        borderRadius: (wizardMode || embedded) ? 0 : 'md',
        boxShadow: (wizardMode || embedded) ? 'none' : 'sm',
        border: (wizardMode || embedded) ? 'none' : undefined,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {!embedded && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography level="h4" sx={{ fontWeight: 500 }}>
          {wizardMode ? 'Enter Your Birth Details' : 'Birth Details'}
        </Typography>
        <Tooltip 
          title="Enter accurate birth details for precise astrological calculations. All fields are required: exact date, precise birth time (within 2 minutes accuracy), and complete place of birth. These details generate your natal chart, which is the foundation for all astrological predictions."
          placement="top"
          arrow
          variant="soft"
          size="sm"
          sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}
        >
          <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
            <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
          </IconButton>
        </Tooltip>
        <AudioGuide
          src="/audio/guide-birth-details.mp3"
          label="Audio Guide"
          title="Listen to a quick guide on entering birth details"
          variant="red"
          onTimeUpdate={handleAudioTime}
        />
      </Box>
      )}
      
      {!wizardMode && (
        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
          This form creates/updates <strong>Family Members</strong> under your account. It does not modify your own profile or role.
        </Typography>
      )}

      <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 1.75 } }}>
        {/* Name */}
        <FormControl data-audio-cue="name">
          <FormLabel>Name</FormLabel>
          <Input
            data-tour="user-form"
            value={state.fullName}
            onChange={e => onChangeField('fullName', e.target.value)}
            placeholder="Full name"
            required
            sx={{ maxWidth: 420 }}
          />
        </FormControl>

        {/* Gender */}
        <FormControl data-audio-cue="gender">
          <FormLabel>Gender</FormLabel>
          <RadioGroup value={state.gender} onChange={(e) => onChangeField('gender', e.target.value as Gender)}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Radio value="male" label="Male" />
              <Radio value="female" label="Female" />
            </Box>
          </RadioGroup>
        </FormControl>

        {/* Relationship — hidden in wizard (always 'Self' for first-time chart) */}
        {!wizardMode && (
        <FormControl data-audio-cue="relationship">
          <FormLabel>Relationship</FormLabel>
          <Select
            value={state.relationship}
            onChange={(_, value) => value && onChangeField('relationship', value as Relationship)}
            sx={{ maxWidth: 420 }}
            required
          >
            <Option value="Self">Self</Option>
            <Option value="Spouse">Spouse</Option>
            <Option value="Father">Father</Option>
            <Option value="Mother">Mother</Option>
            <Option value="Son">Son</Option>
            <Option value="Daughter">Daughter</Option>
            <Option value="Brother">Brother</Option>
            <Option value="Sister">Sister</Option>
            <Option value="Grandfather (Dada)">Grandfather (Dada ji) - Paternal</Option>
            <Option value="Grandmother (Daadi)">Grandmother (Daadi ji) - Paternal</Option>
            <Option value="Grandfather (Nana)">Grandfather (Nana ji) - Maternal</Option>
            <Option value="Grandmother (Naani)">Grandmother (Naani ji) - Maternal</Option>
            <Option value="Uncle">Uncle</Option>
            <Option value="Aunt">Aunt</Option>
            <Option value="Other">Other</Option>
          </Select>
          <FormHelperText>Change if adding a family member, impacts combined predictions</FormHelperText>
        </FormControl>
        )}

        {/* DOB */}
        <Box data-audio-cue="dob">
          <FormLabel>Date of Birth</FormLabel>
          <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, mt: 0.5 }}>
            <Input
              slotProps={{ input: { ref: dayRef, min: 1, max: 31 } }}
              type="number"
              placeholder="DD"
              value={state.day}
              onChange={e => {
                onChangeField('day', e.target.value);
                if (e.target.value.length === 2) monthRef.current?.focus();
              }}
              sx={{ flex: 1, minWidth: 60 }}
              required
            />
            <Input
              slotProps={{ input: { ref: monthRef, min: 1, max: 12 } }}
              type="number"
              placeholder="MM"
              value={state.month}
              onChange={e => {
                onChangeField('month', e.target.value);
                if (e.target.value.length === 2) yearRef.current?.focus();
              }}
              sx={{ flex: 1, minWidth: 60 }}
              required
            />
            <Input
              slotProps={{ input: { ref: yearRef, min: 1900, max: 2100 } }}
              type="number"
              placeholder="YYYY"
              value={state.year}
              onChange={e => {
                onChangeField('year', e.target.value);
                if (e.target.value.length === 4) hourRef.current?.focus();
              }}
              sx={{ flex: 1.5, minWidth: 80 }}
              required
            />
          </Box>
        </Box>

        {/* TOB */}
        <Box data-audio-cue="tob">
          <FormLabel>Time of Birth</FormLabel>
          <Box data-tour="birth-time" sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, mt: 0.5 }}>
            <Input
              slotProps={{ input: { ref: hourRef, min: 0, max: 23 } }}
              type="number"
              placeholder="HH"
              value={state.hour}
              onChange={e => {
                onChangeField('hour', e.target.value);
                if (e.target.value.length === 2) minuteRef.current?.focus();
              }}
              sx={{ flex: 1, minWidth: 60 }}
              required
            />
            <Input
              slotProps={{ input: { ref: minuteRef, min: 0, max: 59 } }}
              type="number"
              placeholder="MM"
              value={state.minute}
              onChange={e => {
                onChangeField('minute', e.target.value);
                if (e.target.value.length === 2) secondRef.current?.focus();
              }}
              sx={{ flex: 1, minWidth: 60 }}
              required
            />
            <Input
              slotProps={{ input: { ref: secondRef, min: 0, max: 59 } }}
              type="number"
              placeholder="SS"
              value={state.second}
              onChange={e => onChangeField('second', e.target.value)}
              sx={{ flex: 1, minWidth: 60 }}
              required
            />
          </Box>
          <FormHelperText>Use 24-hour format (e.g., 6:30 PM = 18:30)</FormHelperText>
        </Box>

        {/* Place */}
        <FormControl data-audio-cue="city">
          <FormLabel>Place of Birth</FormLabel>
        <div data-tour="city-search" style={{ maxWidth: 420 }}>
          <AsyncSelect<CityOption, false>
            cacheOptions
            defaultOptions={POPULAR_CITY_OPTIONS}
            loadOptions={loadCityOptions}
            value={state.place}
            placeholder="Type a city name..."
            isClearable

            // IMPORTANT: capture the user's typed text for better caching
            onInputChange={(val) => {
              setCityInput(val || '');
              return val;
            }}

            // When a city is picked: update state + persist selection (DB + local)
            onChange={(opt) => {
              const picked = (opt as CityOption | null) ?? null;
              onChangeField('place', picked);
              if (picked) {
                // store immediately (non-blocking)
                rememberCitySelection(picked, cityInput).catch(() => {});
                // optional event for any listeners
                try {
                  window.dispatchEvent(new CustomEvent('city:selected', { detail: { city: picked, typed: cityInput } }));
                } catch {}
              }
            }}

            /* Explicit label/value getters (we use a stable composite id) */
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => `${opt.lat},${opt.lon},${opt.tz}`}

            /* We already filter on the server; don't client-filter again */
            filterOption={() => true}

            /* make the dropdown visible above MUI stacks */
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
            menuPosition="fixed"
            menuShouldScrollIntoView={false}

            loadingMessage={() => (
              <div style={{ 
                padding: '8px 12px', 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>🔍</span>
                Searching cities...
              </div>
            )}
            noOptionsMessage={({ inputValue }) =>
              inputValue && inputValue.length < 2 ? 'Keep typing...' : 'No matches'}
          />
        </div>
          <FormHelperText>Pick from the list to store correct timezone, latitude, and longitude. If multiple cities appear, select the one matching your state/region.</FormHelperText>
          {state.place && (
            <Box data-audio-cue="coords" sx={{ mt: 1, p: 1.5, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
              <Typography level="body-sm">
                <strong>Timezone:</strong> {state.place.tz}
                {(() => {
                  const offset = formatUtcOffset(state.place.tz);
                  return offset ? ` (${offset})` : '';
                })()}
              </Typography>
              <Typography level="body-sm">
                <strong>Coordinates:</strong> {state.place.lat.toFixed(4)}°, {state.place.lon.toFixed(4)}°
              </Typography>
            </Box>
          )}

          {/* Manual coordinates fallback */}
          <Box data-audio-cue="cant-find-city" sx={{ mt: 1 }}>
            <Typography
              level="body-xs"
              sx={{ color: 'primary.500', cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
              onClick={() => { setShowManualCoords(v => !v); setManualVerified(null); setManualError(null); }}
            >
              {showManualCoords ? '▲ Hide manual entry' : "Can't find your city? Click here"}
            </Typography>

            {showManualCoords && (() => {
              // Live validation helpers
              const latNum = parseFloat(manualLat);
              const lonNum = parseFloat(manualLon);
              const latOk  = manualLat !== '' && Number.isFinite(latNum) && latNum >= -90  && latNum <= 90;
              const lonOk  = manualLon !== '' && Number.isFinite(lonNum) && lonNum >= -180 && lonNum <= 180;
              const looksSwapped = manualLat !== '' && manualLon !== '' && !latOk && !lonOk
                && Number.isFinite(latNum) && Number.isFinite(lonNum)
                && latNum >= -180 && latNum <= 180   // valid lon range
                && lonNum >= -90  && lonNum <= 90;   // valid lat range
              return (
              <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed', borderColor: 'neutral.300', borderRadius: 'sm', bgcolor: 'background.surface' }}>

                {/* How-to steps */}
                <Box sx={{ mb: 1.5, p: 1.25, bgcolor: '#f0f9ff', borderRadius: 'sm', border: '1px solid #bae6fd' }}>
                  <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: '#0369a1' }}>📍 How to find coordinates on Google Maps:</Typography>
                  <Typography level="body-xs" sx={{ color: '#0c4a6e', lineHeight: 1.6 }}>
                    1. Open <strong>maps.google.com</strong> on your phone or computer<br/>
                    2. Search for your birth city<br/>
                    3. <strong>Right-click</strong> (desktop) or <strong>tap &amp; hold</strong> (mobile) on the city centre<br/>
                    4. The first item in the menu shows two numbers — e.g. <strong>9.9312, 76.2673</strong><br/>
                    5. The <strong>first number</strong> is Latitude · the <strong>second</strong> is Longitude
                  </Typography>
                </Box>

                {/* Field hints */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  <Typography level="body-xs" sx={{ bgcolor: '#fef3c7', px: 0.8, py: '2px', borderRadius: '4px', color: '#92400e' }}>
                    🌐 Latitude: –90 to 90
                  </Typography>
                  <Typography level="body-xs" sx={{ bgcolor: '#fef3c7', px: 0.8, py: '2px', borderRadius: '4px', color: '#92400e' }}>
                    🌐 Longitude: –180 to 180
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <FormControl sx={{ flex: 1, minWidth: 130 }}>
                    <FormLabel sx={{ fontSize: '0.75rem' }}>
                      Latitude <Typography component="span" level="body-xs" sx={{ color: 'text.tertiary' }}>(1st number)</Typography>
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g. 9.9312"
                      value={manualLat}
                      onChange={e => { setManualLat(e.target.value); setManualVerified(null); setManualError(null); }}
                      onKeyDown={e => e.key === 'Enter' && verifyManualCoords()}
                      color={manualLat === '' ? 'neutral' : latOk ? 'success' : 'danger'}
                      sx={manualLat !== '' && !latOk ? { '--Input-focusedHighlight': 'var(--joy-palette-danger-500)' } : {}}
                    />
                    {manualLat !== '' && !latOk && !looksSwapped && (
                      <Typography level="body-xs" sx={{ color: 'danger.500', mt: 0.25 }}>Must be between –90 and 90</Typography>
                    )}
                  </FormControl>

                  {/* Swap button */}
                  <Tooltip title="Swap Latitude ↔ Longitude" variant="soft" placement="top">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => { setManualLat(manualLon); setManualLon(manualLat); setManualVerified(null); setManualError(null); }}
                      sx={{ mb: '1px', flexShrink: 0 }}
                    >
                      ⇄
                    </IconButton>
                  </Tooltip>

                  <FormControl sx={{ flex: 1, minWidth: 130 }}>
                    <FormLabel sx={{ fontSize: '0.75rem' }}>
                      Longitude <Typography component="span" level="body-xs" sx={{ color: 'text.tertiary' }}>(2nd number)</Typography>
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g. 76.2673"
                      value={manualLon}
                      onChange={e => { setManualLon(e.target.value); setManualVerified(null); setManualError(null); }}
                      onKeyDown={e => e.key === 'Enter' && verifyManualCoords()}
                      color={manualLon === '' ? 'neutral' : lonOk ? 'success' : 'danger'}
                    />
                    {manualLon !== '' && !lonOk && !looksSwapped && (
                      <Typography level="body-xs" sx={{ color: 'danger.500', mt: 0.25 }}>Must be between –180 and 180</Typography>
                    )}
                  </FormControl>

                  <Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={verifyManualCoords}
                      disabled={manualVerifying || !manualLat || !manualLon}
                      sx={{
                        px: 2, py: '6px', borderRadius: 'sm', border: 'none', cursor: 'pointer',
                        bgcolor: 'primary.500', color: 'white', fontSize: '0.8rem', fontWeight: 600,
                        opacity: (manualVerifying || !manualLat || !manualLon) ? 0.5 : 1,
                        '&:hover': { bgcolor: 'primary.600' }, mb: '1px'
                      }}
                    >
                      {manualVerifying ? 'Verifying…' : '🔍 Verify'}
                    </Box>
                  </Box>
                </Box>

                {looksSwapped && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 'sm', mb: 1 }}>
                    <Typography level="body-xs" sx={{ color: '#9a3412', flex: 1 }}>
                      🔄 These look swapped — Latitude should be the first (smaller) number from Google Maps.
                    </Typography>
                    <Box
                      component="button" type="button"
                      onClick={() => { setManualLat(manualLon); setManualLon(manualLat); setManualVerified(null); setManualError(null); }}
                      sx={{ px: 1.5, py: '3px', borderRadius: 'sm', border: '1px solid #f97316', bgcolor: 'white', color: '#c2410c', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                    >
                      Fix it ⇄
                    </Box>
                  </Box>
                )}

                {manualError && (
                  <Typography level="body-xs" sx={{ color: showSuggestForm ? 'warning.700' : 'danger.500', mb: 1 }}>
                    {showSuggestForm ? '⚠️' : '⚠️'} {manualError}
                  </Typography>
                )}

                {/* City suggestion form — shown when reverse geocoding fails */}
                {showSuggestForm && !manualVerified && (
                  <Box sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: 'sm', border: '1px solid #fcd34d', mb: 1 }}>
                    <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5, color: '#92400e' }}>
                      📝 Enter the city name to use these coordinates:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <FormControl sx={{ flex: 1, minWidth: 130 }}>
                        <FormLabel sx={{ fontSize: '0.75rem' }}>City / Village name *</FormLabel>
                        <Input
                          size="sm"
                          placeholder="e.g. Kaithal"
                          value={suggestCity}
                          onChange={e => setSuggestCity(e.target.value)}
                          sx={{ fontSize: '0.8rem' }}
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1, minWidth: 130 }}>
                        <FormLabel sx={{ fontSize: '0.75rem' }}>State / Province</FormLabel>
                        <Input
                          size="sm"
                          placeholder="e.g. Haryana"
                          value={suggestState}
                          onChange={e => setSuggestState(e.target.value)}
                          sx={{ fontSize: '0.8rem' }}
                        />
                      </FormControl>
                    </Box>
                    <Typography level="body-xs" sx={{ color: '#78350f', mb: 1 }}>
                      This will add the city to our database (reviewed by admin) and apply these coordinates to your chart immediately.
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => submitCitySuggestion()}
                      disabled={suggestSubmitting || !suggestCity.trim()}
                      sx={{
                        px: 2, py: '5px', borderRadius: 'sm', border: 'none', cursor: 'pointer',
                        bgcolor: suggestSubmitting || !suggestCity.trim() ? 'neutral.300' : 'warning.500',
                        color: 'white', fontSize: '0.8rem', fontWeight: 600,
                        opacity: suggestSubmitting || !suggestCity.trim() ? 0.6 : 1,
                        '&:hover': { bgcolor: 'warning.600' }
                      }}
                    >
                      {suggestSubmitting ? 'Submitting…' : '✓ Use & Add to Database'}
                    </Box>
                  </Box>
                )}

                {manualVerified && (
                  <Box sx={{ p: 1.5, bgcolor: 'success.softBg', borderRadius: 'sm', border: '1px solid', borderColor: 'success.300' }}>
                    <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>📍 Location identified:</Typography>
                    <Typography level="body-sm"><strong>Place:</strong> {manualVerified.label}</Typography>
                    <Typography level="body-sm"><strong>Timezone:</strong> {manualVerified.tz}</Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}><strong>Coordinates:</strong> {parseFloat(manualLat).toFixed(4)}°, {parseFloat(manualLon).toFixed(4)}°</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
                      Please confirm this matches your intended birth city and state before using.
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => applyManualCoords()}
                      sx={{
                        px: 2, py: '5px', borderRadius: 'sm', border: 'none', cursor: 'pointer',
                        bgcolor: 'success.500', color: 'white', fontSize: '0.8rem', fontWeight: 600,
                        '&:hover': { bgcolor: 'success.600' }
                      }}
                    >
                      ✓ Use this location
                    </Box>
                  </Box>
                )}
              </Box>
            )})()}
          </Box>
        </FormControl>

        {/* Action */}
        <Box data-audio-cue="save" sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box component="button" data-tour="save-calculate" type="button" onClick={onSaveAndCalculate} disabled={working}
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: 'primary.500',
              color: 'white',
              border: 'none',
              borderRadius: 'md',
              fontWeight: 500,
              cursor: working ? 'not-allowed' : 'pointer',
              opacity: working ? 0.6 : 1,
              '&:hover': {
                bgcolor: 'primary.600',
              },
            }}
          >
            {working ? 'Working…' : wizardMode ? 'Calculate My Chart →' : 'Save & Calculate'}
          </Box>

          {msg && <Typography level="body-sm" sx={{ color: 'success.600' }}>{msg}</Typography>}
          {err && <Typography level="body-sm" sx={{ color: 'danger.600' }}>{err}</Typography>}
        </Box>
      </Box>

    </Sheet>
  );
}
// ===== END FILE: src/components/UserForm.tsx =====
